import React, { useEffect, useRef, useState } from 'react';
import { useLiveAPIContext } from '../../contexts/LiveAPIContext';
import { useInterview } from '../../contexts/InterviewContext';

interface VideoSocketHandlerProps {
  isActive: boolean; // Whether video capture should be active
  videoRef: React.RefObject<HTMLVideoElement>; // Reference to the video element
  captureInterval?: number; // Interval between frame captures in ms (default: 1000ms)
  isScreenSharing?: boolean; // Whether screen sharing is active
  isImplementorInterview?: boolean; // For implementor interviews, camera is on for proctoring but don't send frames to Gemini
}

/**
 * Component to handle video frame capture and transmission via WebSocket
 */
const VideoSocketHandler: React.FC<VideoSocketHandlerProps> = ({ 
  isActive, 
  videoRef,
  captureInterval = 1000,
  isScreenSharing = false,
  isImplementorInterview = false
}) => {
  const { client, connected } = useLiveAPIContext();
  const { isGeneratingFeedback } = useInterview();
  const [isCapturing, setIsCapturing] = useState(false);
  
  // Refs for video handling
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const captureIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const mountedRef = useRef(true);
  const lastFrameTimeRef = useRef<number>(0);
  
  // Tracking the source of the video stream (for debugging)
  const videoStreamIdRef = useRef<string | null>(null);
  
  // Use the same capture interval for both camera and screen sharing
  const effectiveCaptureInterval = captureInterval;
  
  // Initialize canvas for frame capture and set mounted state
  useEffect(() => {
    // Set the mounted state to true when the component mounts
    mountedRef.current = true;
    
    if (!canvasRef.current) {
      canvasRef.current = document.createElement('canvas');
    }
    
    console.log(`[VideoSocketHandler] Component mounted and initialized, mode: ${isScreenSharing ? 'screen sharing' : 'camera'}`);
    
    return () => {
      // Set mounted state to false when the component unmounts
      mountedRef.current = false;
      
      if (captureIntervalRef.current) {
        clearInterval(captureIntervalRef.current);
        captureIntervalRef.current = null;
      }
      
      console.log(`[VideoSocketHandler] Component unmounted and cleaned up, mode: ${isScreenSharing ? 'screen sharing' : 'camera'}`);
    };
  }, [isScreenSharing]);

  // Handle video capture state based on active state and connection
  useEffect(() => {
    // For implementor interviews, CAMERA should be ON for proctoring but we DON'T send camera frames to Gemini to save tokens
    // However, SCREEN SHARING should still work normally in edge cases where its needed.
    if (isImplementorInterview && !isScreenSharing) {
      console.log(`[VideoSocketHandler] Implementor interview detected - camera is ON for proctoring but NOT sending camera frames to Gemini`);
      // Keep camera on but don't start capturing/sending frames
      if (isCapturing) {
        stopCapturing();
      }
      return;
    }
    
    if (isActive && connected && !isGeneratingFeedback && !isCapturing) {
      startCapturing();
    } else if ((!isActive || !connected || isGeneratingFeedback) && isCapturing) {
      stopCapturing();
    }
    
    // When activated, log information about video stream
    if (isActive && videoRef.current && videoRef.current.srcObject) {
      const stream = videoRef.current.srcObject as MediaStream;
      const videoTracks = stream.getVideoTracks();
      
      if (videoTracks.length > 0) {
        const trackInfo = videoTracks.map(track => ({
          id: track.id,
          label: track.label,
          enabled: track.enabled,
          readyState: track.readyState,
          settings: track.getSettings ? track.getSettings() : {}
        }));
        
        console.log(`[VideoSocketHandler] Video tracks for ${isScreenSharing ? 'screen sharing' : 'camera'}:`, trackInfo);
        
        // Store stream ID for tracking
        videoStreamIdRef.current = stream.id;
      } else {
        console.warn(`[VideoSocketHandler] No video tracks found in ${isScreenSharing ? 'screen sharing' : 'camera'} stream`);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive, connected, isGeneratingFeedback, isCapturing, isScreenSharing, isImplementorInterview]); // Added isImplementorInterview to dependencies

  // Monitor isGeneratingFeedback and stop capturing when true
  useEffect(() => {
    if (isGeneratingFeedback && captureIntervalRef.current) {
      console.log(`[VideoSocketHandler] Stopping video capture during feedback generation`);
      clearInterval(captureIntervalRef.current);
      captureIntervalRef.current = null;
    }
  }, [isGeneratingFeedback]);

  // Check if the client is ready to receive video frames
  const isClientReadyForVideo = () => {
    // Simplified check using connected state from context
    if (!client || !connected) {
      return false;
    }
    
    // Check if video element is valid
    if (!videoRef.current || videoRef.current.videoWidth === 0 || videoRef.current.videoHeight === 0) {
      return false;
    }
    
    // Make sure we have a valid stream with active tracks
    const stream = videoRef.current.srcObject as MediaStream;
    if (!stream) return false;
    
    // Check if any video tracks are live and enabled
    const videoTracks = stream.getVideoTracks();
    if (videoTracks.length === 0) return false;
    
    // More thorough track status check
    const hasLiveTrack = videoTracks.some(track => {
      const isLive = track.readyState === 'live';
      const isEnabled = track.enabled;
      // Also check if the track has valid settings
      const settings = track.getSettings ? track.getSettings() : null;
      const hasValidSettings = settings && (settings.width || 0) > 0 && (settings.height || 0) > 0;
      
      return isLive && isEnabled && hasValidSettings;
    });
    
    if (!hasLiveTrack) {
      // Log warning but only once per track state change
      if (!videoStreamIdRef.current || videoStreamIdRef.current !== 'no-live-tracks') {
        console.warn(`[VideoSocketHandler] No live tracks found in ${isScreenSharing ? 'screen' : 'camera'} stream`);
        const trackDetails = videoTracks.map(t => ({
          label: t.label,
          readyState: t.readyState,
          enabled: t.enabled,
          settings: t.getSettings ? t.getSettings() : {}
        }));
        console.log('[VideoSocketHandler] Track details:', trackDetails);
        
        videoStreamIdRef.current = 'no-live-tracks';
      }
      return false;
    }
    
    // For screen sharing mode, do additional checks
    if (isScreenSharing) {
      // For debugging - check if stream has changed
      if (videoStreamIdRef.current && 
          videoStreamIdRef.current !== 'no-live-tracks' && 
          videoStreamIdRef.current !== stream.id) {
        console.log(`[VideoSocketHandler] Stream changed for ${isScreenSharing ? 'screen' : 'camera'}: ${videoStreamIdRef.current} -> ${stream.id}`);
        videoStreamIdRef.current = stream.id;
      }
    }
    
    // All checks passed
    if (videoStreamIdRef.current === 'no-live-tracks') {
      // We're transitioning from not-ready to ready state
      console.log(`[VideoSocketHandler] ${isScreenSharing ? 'Screen' : 'Camera'} stream now has live tracks`);
      videoStreamIdRef.current = stream.id;
    }
    
    return true;
  };
  
  // Capture and send video frame
  const captureAndSendFrame = () => {
    if (!mountedRef.current || !videoRef.current || !client || !connected || isGeneratingFeedback) {
      return;
    }
    
    try {
      console.log('[VideoSocketHandler] Capturing and sending video frame');
      const video = videoRef.current;
      const canvas = canvasRef.current;
      
      if (!canvas || !video) {
        console.log('[VideoSocketHandler] Canvas or video is not ready');
        return;
      }
      
      // Skip if video is not ready
      if (video.readyState !== video.HAVE_ENOUGH_DATA) {
        console.log('[VideoSocketHandler] Video not ready');
        return;
      }
      
      // Adjust canvas size to match video dimensions
      const width = isScreenSharing ? 640 : 320;  // Use larger size for screen sharing
      const height = Math.round(video.videoHeight * (width / video.videoWidth));
      
      canvas.width = width;
      canvas.height = height;
      
      // Draw video frame to canvas
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        console.log('[VideoSocketHandler] Canvas context is not ready');
        return;
      }
      
      ctx.drawImage(video, 0, 0, width, height);
      
      // Convert to base64 JPEG
      const quality = isScreenSharing ? 0.5 : 0.7; // Lower quality for screen sharing to reduce data
      const base64Data = canvas.toDataURL('image/jpeg', quality);
      
      // Send to server
      if (!connected || !client || isGeneratingFeedback) {
        console.log('[VideoSocketHandler] Not connected or generating feedback');
        return;
      }
      
      try {
        // Modified to use sendRealtimeInput directly 
        client.sendRealtimeInput([{
          mimeType: 'image/jpeg',
          data: base64Data.split(',')[1] // Remove the data URL prefix
        }]);
        console.log('[VideoSocketHandler] Video frame sent successfully');
      } catch (error) {
        console.log('[VideoSocketHandler] Error sending video frame:', error);
        // Suppress WebSocket errors to prevent console spam
        if (!(error instanceof Error) || !error.message.includes('WebSocket')) {
          console.error('[VideoSocketHandler] Error sending video frame:', error);
        }
      }
    } catch (error) {
      console.error('[VideoSocketHandler] Error capturing frame:', error);
    }
  };

  // Start capturing video frames
  const startCapturing = () => {
    if (isCapturing || !mountedRef.current) return;
    
    console.log(`[VideoSocketHandler] Starting ${isScreenSharing ? 'screen' : 'camera'} frame capture...`);
    
    // Start capturing frames at the specified interval
    captureIntervalRef.current = setInterval(() => {
      captureAndSendFrame();
    }, effectiveCaptureInterval);
    
    setIsCapturing(true);
    console.log(`[VideoSocketHandler] ${isScreenSharing ? 'Screen' : 'Camera'} frame capture started`);
  };

  // Stop capturing video frames
  const stopCapturing = () => {
    if (!isCapturing || !mountedRef.current) return;
    
    console.log(`[VideoSocketHandler] Stopping ${isScreenSharing ? 'screen' : 'camera'} frame capture...`);
    
    // Clear the capture interval
    if (captureIntervalRef.current) {
      clearInterval(captureIntervalRef.current);
      captureIntervalRef.current = null;
    }
    
    setIsCapturing(false);
    console.log(`[VideoSocketHandler] ${isScreenSharing ? 'Screen' : 'Camera'} frame capture stopped`);
  };

  // Component doesn't render anything visible
  return null;
};

export default VideoSocketHandler; 